import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { signOut } from 'firebase/auth';
import { auth } from '../../services/firebase';
import { RootState } from '../../store';
import { getTeacherForests, getForestStats } from '../../services/firestore';
import CreateForestModal from './CreateForestModal';
import ForestDashboardView from './ForestDashboardView';
import PersonalProfile from '../profile/PersonalProfile';
import Button from '../common/Button';
import { Forest } from '../../types';
import { ASSET_PATHS } from '../../utils/gameConfig';
import { activityManager } from '../../services/activityManager';

interface TeacherDashboardProps {}

const TeacherDashboard: React.FC<TeacherDashboardProps> = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [activeForests, setActiveForests] = useState<Forest[]>([]);
  const [inactiveForests, setInactiveForests] = useState<Forest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedForest, setSelectedForest] = useState<Forest | null>(null);
  const [showInactiveView, setShowInactiveView] = useState(false);
  const [isInstructorActive, setIsInstructorActive] = useState(false);
  const [lastDataLoad, setLastDataLoad] = useState<Date | null>(null);
  const activityCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const autoLogoutTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  //Start monitor
  useEffect(() => {
    console.log('Starting instructor activity monitoring');
    activityManager.startMonitoring();
    
    //Init state
    setIsInstructorActive(activityManager.isActive());
    
    return () => {
      console.log('Stopping instructor activity monitoring');
      if (activityCheckIntervalRef.current) {
        clearInterval(activityCheckIntervalRef.current);
      }
      if (autoLogoutTimeoutRef.current) {
        clearTimeout(autoLogoutTimeoutRef.current);
      }
    };
  }, []);

  //Data fetch monitor

  useEffect(() => {
    if (!user || user.userType !== 'instructor') return;

    const checkInstructorActivity = () => {
      const currentlyActive = activityManager.isActive();
      const wasActive = isInstructorActive;
      
      console.log('Instructor activity check:', {
        currentlyActive,
        wasActive,
        lastDataLoad: lastDataLoad?.toISOString(),
        timeSinceLastLoad: lastDataLoad ? Date.now() - lastDataLoad.getTime() : 'never'
      });

      if (currentlyActive !== wasActive) {
        setIsInstructorActive(currentlyActive);
        
        if (currentlyActive && !wasActive) {
          //Active
          console.log('Instructor active, loading fresh data');
          loadForests();
        } else if (!currentlyActive && wasActive) {
          //Inactive
          console.log('Instructor inactive, stopping dashboard updates');
        }
      }

      //Auto logout
      if (!currentlyActive) {
        if (!autoLogoutTimeoutRef.current) {
          console.log('Setting auto-logout timer for inactive instructor');
          autoLogoutTimeoutRef.current = setTimeout(() => {
            console.log('Auto-logging out inactive instructor');
            signOut(auth).then(() => {
              window.location.hash = 'login';
            }).catch(console.error);
          }, 300000);
        }
      } else {
        //Clear if active
        if (autoLogoutTimeoutRef.current) {
          clearTimeout(autoLogoutTimeoutRef.current);
          autoLogoutTimeoutRef.current = null;
        }
      }
    };

    //Initial check
    checkInstructorActivity();

    //Cron
    activityCheckIntervalRef.current = setInterval(checkInstructorActivity, 30000);

    return () => {
      if (activityCheckIntervalRef.current) {
        clearInterval(activityCheckIntervalRef.current);
      }
      if (autoLogoutTimeoutRef.current) {
        clearTimeout(autoLogoutTimeoutRef.current);
      }
    };
  }, [user, isInstructorActive, lastDataLoad]);

  /*
  useEffect(() => {
    if (!user || user.userType !== 'instructor') return;

    const checkActivity = () => {
      const isActive = activityManager.isActive();
      
      if (isActive) {
        if (autoLogoutTimeoutRef.current) {
          clearTimeout(autoLogoutTimeoutRef.current);
          autoLogoutTimeoutRef.current = null;
        }
      } else {
        if (!autoLogoutTimeoutRef.current) {
          autoLogoutTimeoutRef.current = setTimeout(() => {
            signOut(auth);
          }, 300000);
        }
      }
    };
    
    activityCheckIntervalRef.current = setInterval(checkActivity, 30000);
    
    return () => {
      if (activityCheckIntervalRef.current) {
        clearInterval(activityCheckIntervalRef.current);
      }
      if (autoLogoutTimeoutRef.current) {
        clearTimeout(autoLogoutTimeoutRef.current);
      }
    };
  }, [user?.uid]);
  */

  //Load forest data
  const loadForests = async () => {
    if (!user || user.userType !== 'instructor') return;
    
    //Active
    if (!activityManager.isActive()) {
      console.log('Instructor inactive, skipping forest loading');
      return;
    }
    
    try {
      setIsLoading(true);
      console.log('Loading forests for active instructor');
      
      const allForests = await getTeacherForests(user.uid);
      const now = new Date();
      
      const active = allForests.filter(forest => 
        !forest.isDraft && forest.isActive && forest.endDate > now
      );
      
      const completed = allForests.filter(forest => 
        !forest.isDraft && (!forest.isActive || forest.endDate <= now)
      );
      
      const drafts = allForests.filter(forest => forest.isDraft);
      
      setActiveForests(active);
      setInactiveForests(completed);
      setLastDataLoad(new Date());
      
      console.log('Forest loading complete:', {
        total: allForests.length,
        drafts: drafts.length,
        active: active.length,
        completed: completed.length,
        timestamp: new Date().toISOString()
      });
      
    } catch (error) {
      console.error('Error loading forests:', error);
    } finally {
      setIsLoading(false);
    }
  };

  //Initial load
  useEffect(() => {
    if (user && user.userType === 'instructor' && activityManager.isActive()) {
      loadForests();
    }
  }, [user]);

  //Redirect
  useEffect(() => {
    if (user && (user.userType !== 'instructor' || !user.isActive)) {
      window.location.hash = 'profile';
      return;
    }
  }, [user]);

  //Create forest
  const handleForestCreated = () => {
    setShowCreateModal(false);
    //Reload if active
    if (activityManager.isActive()) {
      loadForests();
    } else {
      window.location.reload();
    }
  };

  if (!user || user.userType !== 'instructor' || !user.isActive) {
    return (
      <div className="min-h-screen bg-powder-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-powder-800 mb-4">
            {user?.userType !== 'instructor' ? 'Access Denied' : 'Account Pending'}
          </h1>
          <p className="text-powder-600">
            {user?.userType !== 'instructor' 
              ? 'This area is for verified instructors only.'
              : 'Your instructor account is pending approval.'
            }
          </p>
        </div>
      </div>
    );
  }

  //Click into forest
  if (selectedForest) {
    return (
      <ForestDashboardView 
        forest={selectedForest} 
        onBack={() => setSelectedForest(null)}
      />
    );
  }

  //Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-powder-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-sage-200 border-t-sage-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-powder-600">Loading your forests...</p>
          {!isInstructorActive && (
            <p className="text-sm text-orange-600 mt-2">
              Waiting for instructor activity...
            </p>
          )}
        </div>
      </div>
    );
  }

  //Dashboard
  return (
    <div 
      className="min-h-screen flex items-center justify-center p-6"
      style={{
        backgroundImage: `url(${ASSET_PATHS.BACKGROUNDS.MODAL})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="w-[95%] h-[95vh] bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-powder-200 overflow-y-auto">
        <div className="p-6 h-full">
          <div className="max-w-7xl mx-auto h-full flex flex-col space-y-6">
            
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-lg p-6 border border-powder-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-powder-800">
                    Teacher Dashboard
                  </h1>
                  <p className="text-powder-600 mt-1">
                    Welcome back, {user.firstName}! Manage your forest classrooms.
                  </p>
                  {/* Activity indicator */}
                  <div className="flex items-center mt-2 space-x-2">
                    <div className={`w-2 h-2 rounded-full ${isInstructorActive ? 'bg-green-500' : 'bg-red-500'}`} />
                    <span className="text-sm text-gray-600">
                      {isInstructorActive ? 'Active' : 'Inactive'} 
                      {lastDataLoad && ` • Last updated: ${lastDataLoad.toLocaleTimeString()}`}
                    </span>
                  </div>
                </div>
                
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => window.location.hash = 'profile'}
                  >
                    Personal Profile
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => setShowCreateModal(true)}
                  >
                    Create New Forest
                  </Button>
                  {/* Manual refresh button */}
                  <Button
                    variant="outline"
                    onClick={loadForests}
                    disabled={!isInstructorActive}
                  >
                    Refresh Data
                  </Button>
                </div>
              </div>
            </motion.div>

            {/* Inactivity warning */}
            {!isInstructorActive && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-orange-50 border border-orange-200 rounded-lg p-4"
              >
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-orange-500 rounded-full animate-pulse" />
                  <span className="text-orange-800 font-medium">
                    Live updates paused due to inactivity. Move your mouse or click to resume.
                  </span>
                </div>
              </motion.div>
            )}

            {/* Quick Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="grid grid-cols-1 md:grid-cols-4 gap-4"
            >
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-powder-200 text-center">
                <div className="text-3xl font-bold text-sage-700">{activeForests.length}</div>
                <div className="text-sm text-sage-600">Active Forests</div>
              </div>
              
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-powder-200 text-center">
                <div className="text-3xl font-bold text-blue-700">
                  {activeForests.reduce((sum, forest) => sum + (forest.currentStudents || 0), 0)}
                </div>
                <div className="text-sm text-blue-600">Total Students</div>
              </div>
              
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-powder-200 text-center">
                <div className="text-3xl font-bold text-lavender-700">{inactiveForests.length}</div>
                <div className="text-sm text-lavender-600">Completed Forests</div>
              </div>
              
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-powder-200 text-center">
                <div className="text-3xl font-bold text-peach-700">
                  {(activeForests.length + inactiveForests.length)}
                </div>
                <div className="text-sm text-peach-600">Total Forests</div>
                <div className="text-xs text-gray-500 mt-1">(excludes drafts)</div>
              </div>
            </motion.div>

            {/* Active Forests */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-lg p-6 border border-powder-200"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-powder-800">Active Forests</h2>
                {activeForests.length === 0 && (
                  <p className="text-powder-500 italic">No active forests yet</p>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {activeForests.map((forest) => (
                  <motion.div
                    key={forest.id}
                    whileHover={{ scale: 1.02 }}
                    className="border border-powder-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => setSelectedForest(forest)}
                  >
                    <h3 className="font-semibold text-powder-800 mb-2">{forest.name}</h3>
                    <div className="space-y-1 text-sm text-powder-600">
                      <div>Code: <span className="font-mono font-medium">{forest.classCode}</span></div>
                      <div>Students: {forest.currentStudents || 0}/{forest.maxStudents || 'Unlimited'}</div>
                      <div>Started: {new Date(forest.startDate).toLocaleDateString()}</div>
                      <div>Ends: {new Date(forest.endDate).toLocaleDateString()}</div>
                    </div>
                    <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-sage-500 h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${Math.min(100, ((forest.currentStudents || 0) / (forest.maxStudents || 1)) * 100)}%` 
                        }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Inactive Forests */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl shadow-lg p-6 border border-powder-200"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-powder-800">Completed Forests</h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowInactiveView(!showInactiveView)}
                >
                  {showInactiveView ? 'Hide' : `View All (${inactiveForests.length})`}
                </Button>
              </div>
              
              {showInactiveView && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {inactiveForests.map((forest) => (
                    <motion.div
                      key={forest.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      whileHover={{ scale: 1.05 }}
                      className="border border-gray-200 rounded-lg p-3 cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => setSelectedForest(forest)}
                    >
                      <h4 className="font-medium text-powder-800 text-sm mb-1">{forest.name}</h4>
                      <div className="text-xs text-powder-500">
                        <div>{forest.classCode}</div>
                        <div>{new Date(forest.endDate).getFullYear()}</div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>

          </div>
        </div>
      </div>

      {/* Create Forest Modal */}
      {showCreateModal && (
        <CreateForestModal
          onClose={() => setShowCreateModal(false)}
          onForestCreated={handleForestCreated}
          teacherId={user.uid}
        />
      )}
    </div>
  );
};

export default TeacherDashboard;